<template>
  <div class="repair-detail-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('repairDetail.title')" :subtitle="$t('repairDetail.page.subtitle')">
      <template #extra>
        <el-button type="primary" icon="el-icon-printer" @click="printRepairDetail">
          {{ $t('repairDetail.print') }}
        </el-button>
        <el-button icon="el-icon-back" @click="goBack">
          {{ $t('repairDetail.back') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- (2) 报修信息面板 -->
    <div class="ds-panel">
      <div class="ds-panel__header">
        <span class="ds-panel__title">{{ $t('repairDetail.page.infoTitle') }}</span>
      </div>

      <div class="ds-info-grid">
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('repairDetail.orderCode') }}</span>
          <span class="ds-info-grid__value ds-num">{{ repairDetailInfo.repairId || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('repairDetail.repairType') }}</span>
          <span class="ds-info-grid__value">{{ repairDetailInfo.repairTypeName || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('repairDetail.repairer') }}</span>
          <span class="ds-info-grid__value">{{ repairDetailInfo.repairName || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('repairDetail.contact') }}</span>
          <span class="ds-info-grid__value ds-num">{{ repairDetailInfo.tel || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('repairDetail.location') }}</span>
          <span class="ds-info-grid__value">{{ repairDetailInfo.repairObjName || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('repairDetail.appointmentStart') }}</span>
          <span class="ds-info-grid__value ds-num">{{ repairDetailInfo.appointmentTime || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('repairDetail.appointmentEnd') }}</span>
          <span class="ds-info-grid__value ds-num">{{ repairDetailInfo.timeout || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('repairDetail.submitDuration') }}</span>
          <span class="ds-info-grid__value ds-num">{{ repairDetailInfo.submitHours || '0' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('repairDetail.finishTime') }}</span>
          <span class="ds-info-grid__value ds-num">{{ repairDetailInfo.finishTime || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('repairDetail.status') }}</span>
          <span class="ds-info-grid__value">
            <ds-status-tag :label="repairDetailInfo.stateName || '-'" :text="repairDetailInfo.stateName" type="info" />
          </span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('repairDetail.content') }}</span>
          <span class="ds-info-grid__value">{{ repairDetailInfo.context || '-' }}</span>
        </div>
        <div class="ds-info-grid__item" v-if="repairDetailInfo.maintenanceType">
          <span class="ds-info-grid__label">{{ $t('repairDetail.maintenanceType') }}</span>
          <span class="ds-info-grid__value">{{ getMaintenanceTypeName(repairDetailInfo.maintenanceType) }}</span>
        </div>
        <div class="ds-info-grid__item"
          v-if="repairDetailInfo.maintenanceType === '1001' || repairDetailInfo.maintenanceType === '1003'">
          <span class="ds-info-grid__label">{{ $t('repairDetail.materials') }}</span>
          <span class="ds-info-grid__value">{{ repairDetailInfo.repairMaterials || '-' }}</span>
        </div>
        <div class="ds-info-grid__item" v-if="repairDetailInfo.maintenanceType === '1001'">
          <span class="ds-info-grid__label">{{ $t('repairDetail.costDetail') }}</span>
          <span class="ds-info-grid__value ds-num ds-amount">
            {{ repairDetailInfo.repairFee || 0 }} {{ $t('repairDetail.yuan') }}
          </span>
        </div>
        <div class="ds-info-grid__item" v-if="repairDetailInfo.appraiseScore !== '0'">
          <span class="ds-info-grid__label">{{ $t('repairDetail.comprehensiveScore') }}</span>
          <span class="ds-info-grid__value ds-num">
            {{ repairDetailInfo.appraiseScore }} {{ $t('repairDetail.points') }}
          </span>
        </div>
        <div class="ds-info-grid__item" v-if="repairDetailInfo.doorSpeedScore !== '0'">
          <span class="ds-info-grid__label">{{ $t('repairDetail.doorSpeedScore') }}</span>
          <span class="ds-info-grid__value ds-num">
            {{ repairDetailInfo.doorSpeedScore }} {{ $t('repairDetail.points') }}
          </span>
        </div>
        <div class="ds-info-grid__item" v-if="repairDetailInfo.repairmanServiceScore !== '0'">
          <span class="ds-info-grid__label">{{ $t('repairDetail.serviceScore') }}</span>
          <span class="ds-info-grid__value ds-num">
            {{ repairDetailInfo.repairmanServiceScore }} {{ $t('repairDetail.points') }}
          </span>
        </div>
        <div class="ds-info-grid__item" v-if="repairDetailInfo.average !== '0.0'">
          <span class="ds-info-grid__label">{{ $t('repairDetail.avgScore') }}</span>
          <span class="ds-info-grid__value ds-num">
            {{ repairDetailInfo.average }} {{ $t('repairDetail.points') }}
          </span>
        </div>
        <div class="ds-info-grid__item" v-if="repairDetailInfo.visitType">
          <span class="ds-info-grid__label">{{ $t('repairDetail.visitSatisfaction') }}</span>
          <span class="ds-info-grid__value">
            {{ repairDetailInfo.visitType === '1001' ? $t('repairDetail.satisfied') : $t('repairDetail.notSatisfied') }}
          </span>
        </div>
        <div class="ds-info-grid__item" v-if="repairDetailInfo.visitType">
          <span class="ds-info-grid__label">{{ $t('repairDetail.visitContent') }}</span>
          <span class="ds-info-grid__value">{{ repairDetailInfo.visitContext || '-' }}</span>
        </div>
      </div>
    </div>

    <!-- (3) 明细 Tab 面板 -->
    <div class="ds-panel ds-panel--tabs">
      <el-tabs v-model="currentTab" @tab-click="changeTab(currentTab)">
        <el-tab-pane :label="$t('repairDetail.orderPhotos')" name="repairDetailPhotos"></el-tab-pane>
        <el-tab-pane :label="$t('repairDetail.repairItems')" name="repairDetailResource"></el-tab-pane>
        <el-tab-pane :label="$t('repairDetail.orderFlow')" name="repairDetailUser"></el-tab-pane>
        <el-tab-pane :label="$t('repairDetail.orderEvents')" name="repairDetailEvent"></el-tab-pane>
      </el-tabs>
    </div>

    <div class="ds-panel ds-panel--body">
      <repair-detail-photos v-if="currentTab === 'repairDetailPhotos'" ref="repairDetailPhotos"
        :repair-id="repairDetailInfo.repairId"></repair-detail-photos>
      <repair-detail-resource v-if="currentTab === 'repairDetailResource'" ref="repairDetailResource"
        :repair-id="repairDetailInfo.repairId"></repair-detail-resource>
      <repair-detail-user v-if="currentTab === 'repairDetailUser'" ref="repairDetailUser"
        :repair-id="repairDetailInfo.repairId"></repair-detail-user>
      <repair-detail-event v-if="currentTab === 'repairDetailEvent'" ref="repairDetailEvent"
        :repair-id="repairDetailInfo.repairId"></repair-detail-event>
    </div>

    <view-image ref="viewImage"></view-image>
    <reply-repair-appraise ref="replyAppraise"></reply-repair-appraise>
  </div>
</template>

<script>
import { getRepairDetail } from '@/api/work/repairDetailApi'
import RepairDetailPhotos from '@/components/work/repairDetailPhotos'
import RepairDetailResource from '@/components/work/repairDetailResource'
import RepairDetailUser from '@/components/work/repairDetailUser'
import RepairDetailEvent from '@/components/work/repairDetailEvent'
import ViewImage from '@/components/work/viewImage'
import ReplyRepairAppraise from '@/components/work/ReplyRepairAppraise'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'RepairDetailList',
  components: {
    RepairDetailPhotos,
    RepairDetailResource,
    RepairDetailUser,
    RepairDetailEvent,
    ViewImage,
    ReplyRepairAppraise,
    DsPageHeader,
    DsStatusTag
  },
  data() {
    return {
      currentTab: 'repairDetailPhotos',
      repairDetailInfo: {
        repairId: '',
        repairType: '',
        repairTypeName: '',
        repairName: '',
        tel: '',
        roomId: '',
        roomName: '',
        repairObjName: '',
        appointmentTime: '',
        submitHours: '',
        timeout: '',
        finishTime: '',
        context: '',
        stateName: '',
        userId: '',
        userName: '',
        photos: [],
        visitType: '',
        visitContext: '',
        maintenanceType: '',
        repairMaterials: '',
        repairFee: '',
        appraiseScore: 0,
        doorSpeedScore: 0,
        repairmanServiceScore: 0,
        average: 0.0
      }
    }
  },
  created() {
    const repairId = this.$route.query.repairId
    this.repairDetailInfo.repairId = repairId
    this.loadRepairDetail()
  },
  methods: {
    async loadRepairDetail() {
      try {
        const data = await getRepairDetail(this.repairDetailInfo.repairId)
        Object.assign(this.repairDetailInfo, data)
        this.changeTab('repairDetailPhotos')
      } catch (error) {
        this.$message.error(error.message)
      }
    },
    getMaintenanceTypeName(type) {
      if (type === '1001') return this.$t('repairDetail.paidService')
      if (type === '1002') return this.$t('repairDetail.freeService')
      if (type === '1003') return this.$t('repairDetail.needMaterials')
      if (type === '1004') return this.$t('repairDetail.noMaterials')
      return '-'
    },
    goBack() {
      this.$router.go(-1)
    },
    printRepairDetail() {
      window.open(`/#/pages/property/printRepairDetail?repairId=${this.repairDetailInfo.repairId}&repairType=${this.repairDetailInfo.repairType}`)
    },
    changeTab(tab) {
      this.currentTab = tab
      setTimeout(() => {
        if (this.$refs[`${tab}`]) {
          this.$refs[`${tab}`].loadData()
        }
      }, 500)
    }
  }
}
</script>

<style lang="scss" scoped>
.repair-detail-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;
  margin-bottom: var(--ds-spacing-md);

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--ds-spacing-sm) var(--ds-spacing-lg);
    border-bottom: 1px solid var(--ds-gray-200);
  }

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* Tab 面板 */
.ds-panel--tabs {
  padding: 0 var(--ds-spacing-lg);
}

/* 明细内容面板 */
.ds-panel--body {
  padding: var(--ds-spacing-lg);
}

/* 信息栅格 */
.ds-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: var(--ds-spacing-md) var(--ds-spacing-lg);
  padding: var(--ds-spacing-lg);

  &__item {
    display: flex;
    align-items: baseline;
    gap: var(--ds-spacing-sm);
    min-width: 0;
  }

  &__label {
    flex-shrink: 0;
    font-size: var(--ds-font-size-sm);
    color: var(--ds-gray-500);
  }

  &__value {
    font-size: var(--ds-font-size-sm);
    color: var(--ds-gray-900);
    word-break: break-all;
  }
}

/* 金额强调 */
.ds-amount {
  font-weight: 600;
}
</style>
